import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { HelpCircle, ChevronRight, ArrowLeft } from 'lucide-react';

import PublicNavBar from '@/components/landing/PublicNavBar';
import { useSeoMeta } from '@/lib/useSeoMeta';

const API = process.env.REACT_APP_BACKEND_URL;

/* iter-p3-r — Public FAQ page.
 *
 * SEO surface: schema.org FAQPage JSON-LD hydrated from the backend
 * plus per-question meta description.  Deep-links back to landing
 * and to the bookstore so search-traffic converts either way.
 */
export default function FAQPage() {
  const [entries, setEntries] = useState([]);
  const [jsonLd, setJsonLd] = useState(null);

  useSeoMeta('/faq', {
    title: 'Frequently Asked Questions — Crown & Rose',
    description: 'Frequently asked questions about Crown & Rose — the indie-fiction studio and bookstore. Pricing, copyright of AI-assisted work, translation into curated languages, the Craft Multiplier, direct-to-reader sales.',
    keywords: 'indie novel FAQ, AI writing copyright, Craft Multiplier, novel translation, Stripe Connect authors, Crown and Rose, The Loom, The Vault bookstore, direct-to-reader sales, KDP export',
    og_image: 'https://static.prod-images.emergentagent.com/jobs/67d45115-9fac-4a11-82ed-b7a8bff08f0b/images/771a390f686e7a8af84c36140aa52216953e3cdfd50882b3fcac08c2d23814ce.jpeg',
  });

  useEffect(() => {
    axios.get(`${API}/api/faq`).then(r => {
      setEntries(r.data?.entries || []);
      setJsonLd(r.data?.json_ld || null);
    }).catch(() => setEntries([]));
  }, []);

  return (
    <div className="h-full overflow-y-auto bg-background text-foreground" data-testid="faq-page">
      {/* 2026-02 · Left-rail nav is available across marketing
       *  surfaces so FAQ readers keep the Desk / Vault / Margin
       *  shortcuts one click away. */}

      {jsonLd && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      )}
      <PublicNavBar
        onSignIn={() => { window.location.href = '/login'; }}
        onSignUp={() => { window.location.href = '/signup'; }}
      />

      <main className="mx-auto max-w-4xl px-4 py-10 sm:px-6" data-testid="faq-main">
        <div className="mb-8">
          <div className="mb-1 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
            <HelpCircle className="h-3.5 w-3.5" /> Frequently asked
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight">
            Answers before you spin up a novel
          </h1>
          <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-muted-foreground">
            Everything about pricing, copyright of AI-assisted work, translation into curated languages, the Craft Multiplier, and how sales pay out. Missing a question? Open a support ticket from your dashboard and we'll answer it here.
          </p>
        </div>

        <div className="space-y-2" data-testid="faq-list">
          {entries.map((e, i) => (
            <FaqRow key={i} idx={i} q={e.q} a={e.a} />
          ))}
          {entries.length === 0 && (
            <div className="rounded-md border border-dashed border-border p-6 text-center text-[12.5px] text-muted-foreground" data-testid="faq-empty">
              FAQ content is loading…
            </div>
          )}
        </div>

        <div className="mt-12 grid gap-3 sm:grid-cols-2">
          <Link to="/" className="rounded-md border border-border bg-card p-5 hover:border-primary" data-testid="faq-cta-landing">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">← Return to landing</div>
            <div className="mt-1 font-semibold">See The Loom studio + The Vault bookstore in a single view.</div>
          </Link>
          <Link to="/bookstore" className="rounded-md border border-border bg-card p-5 hover:border-primary" data-testid="faq-cta-bookstore">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Browse the shelves →</div>
            <div className="mt-1 font-semibold">Direct-to-reader indie fiction, filterable by language, genre, and AI offerings.</div>
          </Link>
        </div>

        {/* iter55z-audit round 9 (extras 5) — "Back to the Promise" pill.
         *  Sends the shopper back to the trust-badges section on the
         *  landing page — same anchor the ScriptoriumLanding scrolls to
         *  when it detects `#landing-trust-badges` in the URL hash. */}
        <div className="mt-4 flex justify-center">
          <Link
            to="/how-we-help-writers#hwh-trust-badges"
            data-testid="faq-back-to-promise"
            className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-4 py-2 text-[12.5px] font-semibold text-primary transition hover:bg-primary/20"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to the Promise — pick your room
          </Link>
        </div>
      </main>

      <footer className="border-t border-border py-8 text-center text-[11.5px] text-muted-foreground">
        Crown &amp; Rose · Indie fiction, AI-assisted, on your terms.
      </footer>
    </div>
  );
}

function FaqRow({ idx, q, a }) {
  const [open, setOpen] = useState(idx < 3);
  return (
    <details
      open={open}
      onToggle={(e) => setOpen(e.target.open)}
      className="group rounded-md border border-border bg-card px-4 py-3 open:border-primary/40"
      data-testid={`faq-row-${idx}`}
    >
      <summary className="flex cursor-pointer items-start justify-between gap-3 text-[14.5px] font-semibold leading-snug marker:hidden">
        <span>{q}</span>
        <ChevronRight className={`mt-1 h-4 w-4 flex-shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-90' : ''}`} />
      </summary>
      <p className="mt-3 whitespace-pre-line text-[13.5px] leading-relaxed text-muted-foreground" data-testid={`faq-answer-${idx}`}>{a}</p>
    </details>
  );
}
